import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import {
  AdminSideNavigation,
  AdminRoutes,
} from '@/features/admin/navigation/containers/admin-side-navigation';
import ContractWizardTimeline from '../contract-wizard-timeline/ContractWizardTimeline';
import { DropDown } from '@/components/design-system/molecules/dropdown';
import { PhoneInputField } from '@/components/design-system/molecules/phone-input';
import { AddColocFormProps, ColocFormData, ValidationErrors } from './types';
import { validateForm } from './validation';
import { DEFAULT_FORM_DATA, LOCALES } from './constants';
import { FormField } from './FormField';
import { DatePicker } from '@/components/design-system/molecules/datepicker';

const AddColocForm: React.FC<AddColocFormProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ColocFormData>(
    (initialData as ColocFormData) || (DEFAULT_FORM_DATA as ColocFormData)
  );
  const [errors, setErrors] = useState<ValidationErrors>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm(formData, t);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      onSubmit(formData);
    }
  };

  const handlePhoneChange = (value: string, country: any) => {
    const countryCode = country.dialCode;
    const phoneNumber = value.substring(countryCode.length);

    setFormData((prev) => ({
      ...prev,
      colocMobileCountryCode: countryCode,
      colocMobileNumber: phoneNumber,
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="flex h-screen w-screen">
      <div className="flex-shrink-0">
        <AdminSideNavigation tabToSet={AdminRoutes.Contracts} />
      </div>

      <div className="flex h-full flex-1 flex-col bg-gray-50 p-6">
        <div className="border-b border-gray-200">
          <div className="flex items-center text-[14px] font-medium">
            <span className="text-text-primaryLight">
              {t('js.features.admin.contracts.add_coloc_form.page_name')}
            </span>
            <IconSaxIcon name="arrow-right-1" size={16} className="mx-2" />
            <span className="text-N900">
              {t('js.features.admin.contracts.add_coloc_form.create_contact')}
            </span>
          </div>
          <h1 className="mt-2 text-[25px] font-bold leading-loose text-text-primaryHeavy">
            {t('js.features.admin.contracts.add_coloc_form.create_contract')}
          </h1>
        </div>

        <div className="rounded-lg border-b border-gray-200 bg-white px-8 py-4">
          <ContractWizardTimeline currentStep={1} />
        </div>

        <div className="flex-1 overflow-auto py-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-8 rounded-lg bg-white p-6 shadow-sm">
              <div className="mb-6">
                <h2
                  style={{ fontSize: '18px' }}
                  className="font-bold leading-relaxed text-text-primaryHeavy"
                >
                  {t('js.features.admin.contracts.add_coloc_form.whos_moving_in')}
                </h2>
                <p className="mt-1 text-[14px] font-medium text-text-primaryLight">
                  {t('js.features.admin.contracts.add_coloc_form.provide_details')}
                </p>
              </div>

              <h3 className="mb-4 text-[16px] font-bold text-N900">
                {t('js.features.admin.contracts.add_coloc_form.coloc_details')}
              </h3>

              <div className="mb-4 grid w-[500px] grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  label={t('js.features.admin.contracts.add_coloc_form.first_name')}
                  name="colocFirstName"
                  value={formData.colocFirstName}
                  onChange={handleInputChange}
                  error={errors.colocFirstName}
                  placeholder="Coloc first name"
                />
                <FormField
                  label={t('js.features.admin.contracts.add_coloc_form.last_name')}
                  name="colocLastName"
                  value={formData.colocLastName}
                  onChange={handleInputChange}
                  error={errors.colocLastName}
                  placeholder="Coloc last name"
                />
              </div>

              <div className="mb-4 mt-4 flex w-[650px] gap-4">
                <FormField
                  label={t('js.features.admin.contracts.add_coloc_form.email')}
                  name="colocEmail"
                  type="email"
                  value={formData.colocEmail}
                  onChange={handleInputChange}
                  placeholder="coloc@example.com"
                  error={errors.colocEmail}
                  className="flex-1"
                />

                <div className="flex-1">
                  <PhoneInputField
                    value={`+${formData.colocMobileCountryCode}${formData.colocMobileNumber}`}
                    onChange={handlePhoneChange}
                    error={errors.colocMobileNumber}
                    label={
                      t('js.features.admin.contracts.add_coloc_form.mobile_number') || undefined
                    }
                    defaultCountry="be"
                    placeholder="1234-567"
                  />
                </div>

                <div className="flex-1">
                  <label className="mb-1 block text-[12px] font-medium text-N900">
                    {t('js.features.admin.contracts.add_coloc_form.native_language')}
                  </label>
                  <DropDown
                    value={formData.colocLocale}
                    onChange={(value: string) =>
                      setFormData((prev) => ({ ...prev, colocLocale: value }))
                    }
                    className="w-full"
                    label={t('js.features.admin.contracts.add_coloc_form.native_language')}
                    showLabel={false}
                    displayValue={
                      LOCALES.find((locale) => locale.value === formData.colocLocale)?.label ||
                      'English'
                    }
                  >
                    <DropDown.DropDownOptions>
                      {LOCALES.map((option) => (
                        <DropDown.DropDownOption key={option.value} value={option.value}>
                          <DropDown.DropDownOptionDefaultContent>
                            {option.label}
                          </DropDown.DropDownOptionDefaultContent>
                        </DropDown.DropDownOption>
                      ))}
                    </DropDown.DropDownOptions>
                  </DropDown>
                </div>
              </div>

              <FormField
                label={t('js.features.admin.contracts.add_coloc_form.coloc_id')}
                name="colocId"
                value={formData.colocId}
                onChange={handleInputChange}
                error={errors.colocId}
                className="mb-4 w-[250px]"
                showHelpText
                helpText={
                  t('js.features.admin.contracts.add_coloc_form.coloc_id_help') || undefined
                }
                placeholder="Enter ID"
              />

              <div className="mb-4 mt-2 grid w-[650px] grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  label={t('js.features.admin.contracts.add_coloc_form.birthplace')}
                  name="colocBirthplace"
                  value={formData.colocBirthplace}
                  onChange={handleInputChange}
                  error={errors.colocBirthplace}
                  placeholder="City, Country"
                />
                <div className="w-full">
                  <label className="mb-1 block text-[12px] font-medium text-N900">
                    {t('js.features.admin.contracts.add_coloc_form.birthday')}
                  </label>
                  <DatePicker
                    type="date"
                    placeholder={
                      t('js.components.datepicker.placeholder_date_picker') || 'dd/mm/yyyy'
                    }
                    className="w-full"
                    onChange={(date: Date) =>
                      setFormData((prev) => ({ ...prev, colocBirthday: date }))
                    }
                    value={formData.colocBirthday ?? undefined}
                    label={t('js.features.admin.contracts.add_coloc_form.birthday')}
                    showLabel={false}
                    invalid={Boolean(errors.colocBirthday)}
                    errorMessage={errors.colocBirthday || undefined}
                  />
                </div>
              </div>

              <h3 className="mb-3 text-[16px] font-bold text-N900">
                {t('js.features.admin.contracts.add_coloc_form.deposit')}
              </h3>

              <div className="grid w-[500px] grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  label={t('js.features.admin.contracts.add_coloc_form.total_deposit_amount')}
                  name="securityDepositAmount"
                  value={formData.securityDepositAmount}
                  onChange={handleInputChange}
                  error={errors.securityDepositAmount}
                  prefix="€"
                  placeholder="€ Amount"
                />
                <FormField
                  label={t('js.features.admin.contracts.add_coloc_form.paid_deposit_amount')}
                  name="securityDepositAmountPaid"
                  value={formData.securityDepositAmountPaid}
                  onChange={handleInputChange}
                  error={errors.securityDepositAmountPaid}
                  placeholder="€ Amount"
                />
              </div>

              <div className="mt-4 flex justify-between">
                <button onClick={onClose} className="mb-5 border-b bg-transparent text-[16px]">
                  {t('js.features.admin.contracts.add_coloc_form.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex h-[48px] w-[128px] items-center justify-center gap-1 rounded-[8px] bg-R300 px-4 py-[12px] font-sans text-[16px] font-medium leading-[20px] text-[#212529] hover:bg-R400 disabled:bg-E50 disabled:text-E75"
                >
                  {t('js.features.admin.contracts.add_coloc_form.next')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddColocForm;
